import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";
import WorldMap from "@/components/WorldMap";
import { Reveal, Overline } from "@/components/Reveal";
import { REGIONS, IMPORT_ORIGINS, IMAGES } from "@/lib/data";
import { SITE_URL } from "@/lib/site";
import { useLang } from "@/i18n";
import PillBadge from "@/components/PillBadge";

const schema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "Farmsons",
  url: `${SITE_URL}/sourcing`,
  address: { "@type": "PostalAddress", addressLocality: "Kolkata", addressRegion: "West Bengal", addressCountry: "IN" },
  areaServed: ["Himachal Pradesh", "Kashmir", "West Bengal", "Andhra Pradesh", "Karnataka"],
};

export default function Sourcing() {
  const { t, data } = useLang();
  const origins = data.importOrigins || IMPORT_ORIGINS;

  return (
    <>
      <SEO
        title="Sourcing & Regions — Himalayan Apples to Gangetic Mangoes | Farmsons"
        description="Farmsons sources apples from Himachal Pradesh (Shimla, Kinnaur, Kullu) and Kashmir (Shopian, Sopore), mangoes from Malda, Murshidabad, Andhra & Karnataka, and imports from eight origin countries."
        keywords="Himachal apple sourcing, Kashmir apples, Malda mango, Murshidabad mango, apple import origins, Farmsons sourcing"
        jsonLd={schema}
      />
      <PageHeader
        overline={t("sourcing.overline")}
        title={t("sourcing.title")}
        lede={t("sourcing.lede")}
        image={IMAGES.gangetic}
        imageAlt="Aerial view of the green Gangetic delta farmland in West Bengal's mango belt"
      />

      <section className="pb-16 bg-[#fdfbf5]">
        <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12">
          <WorldMap />
          <p className="text-sm text-[#5f6b57] mt-6">
            <span className="text-[#d3a53a]">●</span> {t("sourcing.legendHQ")} &nbsp; <span className="text-[#1d3a1f]">●</span> {t("sourcing.legendOrigins")}
          </p>
        </div>
      </section>

      <section className="pb-24 md:pb-32 bg-[#fdfbf5]">
        <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6 mb-16">
            {REGIONS.map((r, i) => {
              const dr = data.regions?.[i];
              return (
                <Reveal key={r.name} delay={i * 0.06}>
                  <article data-testid={`region-card-${i}`} className="group h-full bg-white border border-[#1d3a1f]/10 rounded-2xl overflow-hidden card-soft hover:border-[#d3a53a] hover:-translate-y-1.5 hover:shadow-xl transition-all duration-300 flex flex-col sm:flex-row">
                    <div className="relative sm:w-2/5 h-48 sm:h-auto overflow-hidden">
                      <img src={r.img} alt={r.alt} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="p-6 sm:w-3/5">
                      <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#d3a53a]">{dr?.type || r.type}</span>
                      <h2 className="mt-1 font-display font-bold text-xl text-[#1d3a1f]">{dr?.name || r.name}</h2>
                      <p className="mt-2 text-sm text-[#5f6b57]">{dr?.note || r.note}</p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {/* Cultivar names stay in Latin — they are trade identifiers */}
                        {r.varieties.map((v) => (
                          <PillBadge key={v} tone={i < 2 ? "red" : "amber"}>{v}</PillBadge>
                        ))}
                      </div>
                    </div>
                  </article>
                </Reveal>
              );
            })}
          </div>

          <Reveal>
            <div className="border-t border-[#1d3a1f]/10 pt-10">
              <Overline>{t("sourcing.importOverline")}</Overline>
              <div className="mt-6 flex flex-wrap gap-x-8 gap-y-4">
                {origins.map((c, i) => (
                  <motion.span key={c} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05, duration: 0.5 }}
                    className="font-display font-bold text-2xl md:text-4xl text-[#1d3a1f]/40 hover:text-[#1d3a1f] transition-colors duration-300 cursor-default">
                    {c}
                  </motion.span>
                ))}
              </div>
              <Link to="/fruit-trading" className="mt-10 inline-block text-sm font-bold text-[#d3a53a] hover:text-[#1d3a1f] transition-colors">{t("sourcing.tradeLink")} →</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
